import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, Clock, CheckCircle2, Upload, RefreshCw, FileWarning,
  ChevronRight, Calendar, AlertCircle, X, Image
} from "lucide-react";
import { toast } from "sonner";

export default function CAPResolutionPage() {
  const { user } = useAuth();
  const [caps, setCaps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolve, setShowResolve] = useState(null);

  const fetchCaps = async () => {
    setLoading(true);
    try {
      const res = await api.get("/audit/caps/my-caps");
      setCaps(res.data.items || []);
      setStats({
        total: res.data.total,
        overdue: res.data.overdue_count,
        urgent: res.data.urgent_count
      });
    } catch (e) {
      toast.error("Failed to load CAPs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCaps(); }, []);

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const dl = new Date(deadline);
    const diff = dl - now;
    
    if (diff < 0) {
      const hours = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
      if (hours < 24) return `${hours}h overdue`;
      return `${Math.floor(hours / 24)}d overdue`;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h remaining`;
    return `${Math.floor(hours / 24)}d remaining`;
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="cap-resolution-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            My Corrective Actions
          </h1>
          <p className="text-slate-500 text-sm">Resolve audit non-conformances</p>
        </div>
        <button onClick={fetchCaps} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50 self-start">
          <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
        </button>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-xs text-slate-500 uppercase font-bold">Total Open</div>
            <div className="text-3xl font-black text-slate-800">{stats.total}</div>
          </div>
          <div className={`border rounded-sm p-4 ${stats.urgent > 0 ? "bg-red-50 border-red-300" : "bg-white border-slate-200"}`}>
            <div className="text-xs text-red-600 uppercase font-bold">Urgent (24h)</div>
            <div className="text-3xl font-black text-red-600">{stats.urgent}</div>
          </div>
          <div className={`border rounded-sm p-4 ${stats.overdue > 0 ? "bg-yellow-50 border-yellow-300" : "bg-white border-slate-200"}`}>
            <div className="text-xs text-yellow-600 uppercase font-bold">Overdue</div>
            <div className="text-3xl font-black text-yellow-600">{stats.overdue}</div>
          </div>
        </div>
      )}

      {/* Overdue Alert */}
      {stats && stats.overdue > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-bold text-red-700">Overdue Corrective Actions</div>
            <div className="text-sm text-red-600">
              You have {stats.overdue} CAP(s) past their deadline. Please resolve immediately.
            </div>
          </div>
        </div>
      )}

      {/* CAPs List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
            <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
            <p className="text-slate-500">Loading corrective actions...</p>
          </div>
        ) : caps.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
            <CheckCircle2 size={32} className="mx-auto text-green-500 mb-2" />
            <p className="text-slate-600 font-medium">All Clear!</p>
            <p className="text-sm text-slate-500">You have no pending corrective actions.</p>
          </div>
        ) : (
          caps.map(cap => (
            <div
              key={cap.id}
              className={`bg-white border-2 rounded-sm overflow-hidden ${
                cap.severity === "URGENT" ? "border-red-400" : "border-yellow-400"
              }`}
            >
              {/* Severity Banner */}
              <div className={`px-4 py-2 flex items-center justify-between ${
                cap.severity === "URGENT" ? "bg-red-500 text-white" : "bg-yellow-500 text-white"
              }`}>
                <div className="flex items-center gap-2">
                  {cap.severity === "URGENT" ? (
                    <AlertTriangle size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  <span className="font-bold text-sm">{cap.severity} - {cap.severity === "URGENT" ? "24 Hour Deadline" : "7 Day Deadline"}</span>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <Clock size={14} />
                  <span className={isOverdue(cap.deadline) ? "font-bold" : ""}>
                    {getTimeRemaining(cap.deadline)}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Question */}
                <p className="font-medium text-slate-800 mb-3">{cap.question_text}</p>
                
                {/* Audit Remarks */}
                {cap.remarks && (
                  <div className="bg-slate-50 rounded-sm p-3 mb-3">
                    <div className="text-xs font-bold text-slate-500 uppercase mb-1">Auditor's Remark</div>
                    <p className="text-sm text-slate-600">{cap.remarks}</p>
                  </div>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    Deadline: {new Date(cap.deadline).toLocaleString()}
                  </span>
                  {cap.audit_info && (
                    <span>Audit: {cap.audit_info.circle_name} ({new Date(cap.audit_info.audit_date).toLocaleDateString()})</span>
                  )}
                </div>

                {/* Status & Action */}
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    cap.status === "OPEN" ? "bg-slate-100 text-slate-600" :
                    cap.status === "UNDER_REVIEW" ? "bg-purple-100 text-purple-600" :
                    "bg-green-100 text-green-600"
                  }`}>
                    {cap.status === "OPEN" ? "Action Required" : cap.status === "UNDER_REVIEW" ? "Under Review" : "Resolved"}
                  </span>
                  
                  {cap.status === "OPEN" && (
                    <button
                      onClick={() => setShowResolve(cap)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
                    >
                      <Upload size={14} />
                      Submit Resolution
                    </button>
                  )}
                  
                  {cap.status === "UNDER_REVIEW" && (
                    <span className="text-sm text-purple-600 font-medium">
                      Awaiting management approval...
                    </span>
                  )}
                </div>

                {/* Resolution Info (if submitted) */}
                {cap.resolution_remarks && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-xs font-bold text-slate-500 uppercase mb-1">Your Resolution</div>
                    <p className="text-sm text-slate-600">{cap.resolution_remarks}</p>
                    {cap.resolution_proof_url && (
                      <a href={cap.resolution_proof_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline">
                        <Image size={14} /> View Proof
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Resolve Modal */}
      {showResolve && (
        <ResolveModal
          cap={showResolve}
          onClose={() => setShowResolve(null)}
          onResolved={() => { setShowResolve(null); fetchCaps(); }}
        />
      )}
    </div>
  );
}

// Resolution Modal
function ResolveModal({ cap, onClose, onResolved }) {
  const [form, setForm] = useState({
    resolution_remarks: "",
    resolution_proof_url: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.resolution_remarks.trim()) {
      toast.error("Please provide resolution remarks");
      return;
    }
    if (!form.resolution_proof_url.trim()) {
      toast.error("Please provide proof URL (photo/document)");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/audit/caps/${cap.id}/resolve`, form);
      toast.success("Resolution submitted for review");
      onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit resolution");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-sm w-full max-w-lg shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-bold">Submit Resolution</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-6">
          {/* CAP Info */}
          <div className={`p-4 rounded-sm mb-4 ${cap.severity === "URGENT" ? "bg-red-50 border border-red-200" : "bg-yellow-50 border border-yellow-200"}`}>
            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Non-Conformance</div>
            <p className="text-slate-800">{cap.question_text}</p>
            {cap.remarks && (
              <p className="text-sm text-slate-600 mt-2 italic">"{cap.remarks}"</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="field-label">Mitigation Remarks *</label>
              <textarea
                className="field-input"
                rows={4}
                required
                placeholder="Describe the corrective action taken to address this non-conformance..."
                value={form.resolution_remarks}
                onChange={e => setForm({ ...form, resolution_remarks: e.target.value })}
              />
            </div>

            <div>
              <label className="field-label">Resolution Proof URL *</label>
              <input
                type="url"
                className="field-input"
                required
                placeholder="https://... (photo or document URL)"
                value={form.resolution_proof_url}
                onChange={e => setForm({ ...form, resolution_proof_url: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload proof photo/document and paste the URL here
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold disabled:opacity-60"
              >
                {loading ? "Submitting..." : "Submit for Review"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
